"use client"

import { t } from "@/languages/i18n"
import type { Career, CareerLocalizationRequest } from "@/types/codegen/data-contracts"
import type { FormInstance } from "antd"
import { Button, Form, Input, Select } from "antd"
import React, { useEffect } from "react"
import CKEditorWrapper from "../../../../lib/config/lib/CkeditorWrapper"

const { TextArea } = Input

export type CareerFormValues = Pick<
  CareerLocalizationRequest,
  "position" | "company" | "location" | "jobType" | "branch" | "expiredDate"
> & {
  content?: string
  metaTitle?: string
  metaDescription?: string
  keywords?: string
}

type CareerFormInitialData = Partial<Career> & {
  content?: string
  seo?: {
    metaTitle?: string
    metaDescription?: string
    keywords?: string
  }
}

export type CareerFormProps = {
  form: FormInstance<CareerFormValues>
  onSubmit: (values: CareerFormValues) => void
  locale?: string
  isEdit?: boolean
  initialData?: CareerFormInitialData | null
  onValuesChange?: () => void
}

const CareerForm: React.FC<CareerFormProps> = ({ form, onSubmit, locale = "vi", isEdit = false, initialData, onValuesChange }) => {
  useEffect(() => {
    if (!initialData) return

    form.setFieldsValue({
      position: initialData.position,
      company: initialData.company,
      location: initialData.location,
      jobType: initialData.jobType,
      branch: initialData.branch,
      expiredDate: initialData.expiredDate,
      content: initialData.content,
      metaTitle: initialData.seo?.metaTitle,
      metaDescription: initialData.seo?.metaDescription,
      keywords: initialData.seo?.keywords,
    })
  }, [initialData, form])

  const handleFinish = () => {
    onSubmit(form.getFieldsValue())
  }

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} className="w-full" onValuesChange={onValuesChange}>
      <Form.Item
        name="position"
        label={t("Vị trí tuyển dụng")}
        rules={[{ required: true, message: t("Vui lòng nhập vị trí") }]}
      >
        <Input />
      </Form.Item>

      <Form.Item name="company" label={t("Công ty")}>
        <Input />
      </Form.Item>

      <Form.Item name="location" label={t("Địa điểm")}>
        <Input />
      </Form.Item>

      <Form.Item
        name="jobType"
        label={t("Loại công việc")}
        rules={[{ required: true, message: t("Vui lòng nhập loại công việc") }]}
      >
        <Select
          placeholder={t("Chọn loại công việc")}
          allowClear
          options={[
            { label: t("Toàn thời gian"), value: "fulltime" },
            { label: t("Bán thời gian"), value: "parttime" },
            { label: t("Remote"), value: "remote" },
          ]}
        />
      </Form.Item>

      <Form.Item name="branch" label={t("Chi nhánh")}>
        <Input />
      </Form.Item>

      <Form.Item name="expiredDate" label={t("Ngày hết hạn")}>
        <Input type="date" />
      </Form.Item>

      <Form.Item name="content" label={t("Nội dung")}>
        <CKEditorWrapper
          value={form.getFieldValue("content")}
          onChange={(val: string) => {
            form.setFieldsValue({ content: val })
            if (onValuesChange) onValuesChange();
          }}
        />
      </Form.Item>

      <div className="border rounded p-4">
        <b>{t("SEO")}</b>

        <Form.Item name="metaTitle" label={t("Meta title")}>
          <Input />
        </Form.Item>

        <Form.Item name="metaDescription" label={t("Meta description")}>
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item name="keywords" label={t("Keywords")}>
          <Input />
        </Form.Item>
      </div>

      <Button type="primary" htmlType="submit" className="mt-4">
        {t("Lưu")}
      </Button>
    </Form>
  )
}

export default CareerForm
